import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Save, Loader2, Eye, EyeOff, Wifi, CreditCard, ArrowLeft, ShieldCheck, Lock } from 'lucide-react';
import { toast } from '../../lib/safeToast';

const ENV_FIELDS = [
  { key: 'base_url', label: 'Base API URL', placeholder: 'https://api.example.com', type: 'text' },
  { key: 'callback_url', label: 'Callback URL', placeholder: 'https://your-app.com/callback', type: 'text' },
  { key: 'username', label: 'Username', placeholder: 'API username', type: 'text' },
  { key: 'access_key', label: 'Access Key', placeholder: 'API access key', type: 'secret' },
  { key: 'secret_key', label: 'Secret Key', placeholder: 'API secret key', type: 'secret' },
  { key: 'public_key', label: 'Public Key', placeholder: 'Optional public key', type: 'secret' },
  { key: 'webhook_secret', label: 'Webhook Secret Token', placeholder: 'Webhook secret', type: 'secret' },
  { key: 'encryption_method', label: 'Encryption Method', placeholder: 'e.g. HMAC-SHA256', type: 'text' },
];

const SECRET_FIELD_KEYS = ENV_FIELDS.filter((f) => f.type === 'secret').map((f) => f.key);
const EMPTY_SECTION = { base_url: '', callback_url: '', username: '', access_key: '', secret_key: '', public_key: '', webhook_secret: '', encryption_method: '' };

function SecretInput({ value, onChange, placeholder, onRevealClick, revealed }) {
  const [visible, setVisible] = useState(false);
  const isMasked = value === '••••••••';

  const handleEyeClick = () => {
    if (isMasked && !revealed) {
      onRevealClick?.();
      return;
    }
    setVisible(!visible);
  };

  return (
    <div className="relative">
      <Input
        type={visible || revealed ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="input-field pr-10"
        autoComplete="off"
      />
      <button
        type="button"
        onClick={handleEyeClick}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
        tabIndex={-1}
        title={isMasked && !revealed ? 'Verify 2FA to reveal' : visible ? 'Hide' : 'Show'}
      >
        {isMasked && !revealed ? (
          <Lock className="w-4 h-4 text-amber-500" />
        ) : (visible || revealed) && !isMasked ? (
          <EyeOff className="w-4 h-4" />
        ) : (
          <Eye className="w-4 h-4" />
        )}
      </button>
    </div>
  );
}

function EnvSection({ title, badge, data, onChange, secretsRevealed, onRevealClick }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full ${
          badge === 'sandbox'
            ? 'bg-amber-100 text-amber-800 border border-amber-300'
            : 'bg-emerald-100 text-emerald-800 border border-emerald-300'
        }`}>
          {title}
        </span>
        {!secretsRevealed && data && SECRET_FIELD_KEYS.some((k) => data[k] === '••••••••') && (
          <button
            type="button"
            onClick={onRevealClick}
            className="flex items-center gap-1.5 text-xs text-amber-700 hover:text-amber-900 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-full px-3 py-1 transition-colors"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            Verify 2FA to view secrets
          </button>
        )}
        {secretsRevealed && (
          <span className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
            <ShieldCheck className="w-3.5 h-3.5" />
            Secrets revealed
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        {ENV_FIELDS.map((field) => (
          <div key={field.key}>
            <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide">{field.label}</Label>
            {field.type === 'secret' ? (
              <SecretInput
                value={data[field.key] || ''}
                onChange={(e) => onChange(field.key, e.target.value)}
                placeholder={field.placeholder}
                revealed={secretsRevealed}
                onRevealClick={onRevealClick}
              />
            ) : (
              <Input
                type="text"
                value={data[field.key] || ''}
                onChange={(e) => onChange(field.key, e.target.value)}
                placeholder={field.placeholder}
                className="input-field"
                autoComplete="off"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BackOfficeConfigPGDetail() {
  const { code } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSandbox, setTestingSandbox] = useState(false);
  const [testingProd, setTestingProd] = useState(false);

  // 2FA reveal state
  const [twoFaDialogOpen, setTwoFaDialogOpen] = useState(false);
  const [twoFaCode, setTwoFaCode] = useState('');
  const [twoFaVerifying, setTwoFaVerifying] = useState(false);
  const [secretsRevealed, setSecretsRevealed] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/backoffice/pg-config');
      const list = res.data?.data || [];
      const gw = list.find((c) => c.gateway_code === code);
      if (gw) {
        setConfig({
          name: gw.name || code,
          description: gw.description || '',
          is_active: gw.is_active !== false,
          sandbox: { ...EMPTY_SECTION, ...(gw.sandbox || {}) },
          production: { ...EMPTY_SECTION, ...(gw.production || {}) },
        });
      } else {
        setConfig(null);
      }
    } catch {
      toast.error('Failed to load gateway config');
    } finally {
      setLoading(false);
    }
  }, [api, code]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSectionChange = (envKey, fieldKey, value) => {
    setConfig((prev) => ({
      ...prev,
      [envKey]: { ...prev[envKey], [fieldKey]: value },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/backoffice/pg-config/${code}`, {
        name: config.name,
        description: config.description,
        is_active: config.is_active,
        sandbox: config.sandbox,
        production: config.production,
      });
      toast.success('Payment gateway configuration saved');
      setSecretsRevealed(false);
      await fetchConfig();
    } catch {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (envKey) => {
    const setTesting = envKey === 'sandbox' ? setTestingSandbox : setTestingProd;
    setTesting(true);
    try {
      const section = config[envKey] || {};
      const res = await api.post(`/backoffice/pg-config/${code}/test`, {
        environment: envKey,
        credentials: {
          base_url: section.base_url,
          username: section.username,
          access_key: section.access_key,
          secret_key: section.secret_key,
        },
      });
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch {
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleRevealClick = () => {
    if (!user?.is_2fa_enabled) {
      toast.error('You must enable 2FA before viewing API secrets. Go to Settings → Security to enable 2FA.', { duration: 6000 });
      return;
    }
    setTwoFaCode('');
    setTwoFaDialogOpen(true);
  };

  const handleTwoFaVerify = async () => {
    if (!twoFaCode.trim()) return;
    setTwoFaVerifying(true);
    try {
      const res = await api.post(`/backoffice/pg-config/${code}/reveal-secrets`, {
        code: twoFaCode.trim(),
      });
      const revealed = res.data?.data || {};
      setConfig((prev) => {
        const updated = { ...prev };
        for (const envKey of ['sandbox', 'production']) {
          if (revealed[envKey]) {
            const section = { ...updated[envKey] };
            for (const field of SECRET_FIELD_KEYS) {
              if (revealed[envKey][field]) {
                section[field] = revealed[envKey][field];
              }
            }
            updated[envKey] = section;
          }
        }
        return updated;
      });
      setSecretsRevealed(true);
      setTwoFaDialogOpen(false);
      setTwoFaCode('');
      toast.success('API secrets revealed successfully');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Verification failed';
      toast.error(msg);
    } finally {
      setTwoFaVerifying(false);
    }
  };

  return (
    <BackOfficeLayout>
      <div className="space-y-8">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/backoffice/config/pg')}
            className="text-slate-600 hover:text-[#0B6445] hover:bg-[#E6F7F3] -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            All Gateways
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#0B6445]" />
          </div>
        ) : !config ? (
          <Card>
            <CardContent className="py-16 text-center text-slate-500">
              <CreditCard className="w-10 h-10 mx-auto mb-3 text-slate-400" />
              <p className="text-lg font-medium">Gateway not found</p>
              <p className="text-sm mt-1">No configuration exists for code &quot;{code}&quot;.</p>
              <Button
                variant="outline"
                className="mt-4 border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                onClick={() => navigate('/backoffice/config/pg')}
              >
                Back to gateways
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <div className="flex items-start justify-between pb-4">
              <div>
                <h1 className="text-2xl font-bold text-[#0F172A]">Edit Payment Gateway</h1>
                <p className="text-slate-600 mt-2">
                  Configure API credentials for <span className="font-semibold">{config.name}</span>. Backend reads from this config and falls back to environment variables.
                </p>
              </div>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#0B6445] hover:bg-[#095b3d] text-white px-6 shrink-0 ml-4"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </div>

            {/* Gateway Identity */}
            <Card>
              <CardHeader className="pb-3 pt-6">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#0B6445]" />
                  Gateway Identity
                </CardTitle>
                <CardDescription>General information about this payment gateway</CardDescription>
              </CardHeader>
              <CardContent className="pb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 max-w-2xl">
                  <div>
                    <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Gateway Name</Label>
                    <Input
                      value={config.name}
                      onChange={(e) => setConfig((p) => ({ ...p, name: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Gateway Code</Label>
                    <Input value={code} disabled className="input-field bg-slate-50 text-slate-500 font-mono" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Description</Label>
                    <Input
                      value={config.description}
                      onChange={(e) => setConfig((p) => ({ ...p, description: e.target.value }))}
                      placeholder="Payment gateway description"
                      className="input-field"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* API & Endpoints */}
            <Card>
              <CardHeader className="pb-3 pt-6">
                <CardTitle className="text-base font-semibold">API &amp; Endpoints</CardTitle>
                <CardDescription>Configure sandbox and production API credentials separately</CardDescription>
              </CardHeader>
              <CardContent className="pb-6 space-y-8">
                {/* Sandbox */}
                <div>
                  <EnvSection
                    title="Sandbox Configuration"
                    badge="sandbox"
                    data={config.sandbox}
                    onChange={(k, v) => handleSectionChange('sandbox', k, v)}
                    secretsRevealed={secretsRevealed}
                    onRevealClick={handleRevealClick}
                  />
                  <div className="mt-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={testingSandbox || !config.sandbox.base_url}
                      onClick={() => handleTest('sandbox')}
                      className="border-amber-400 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                    >
                      {testingSandbox ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wifi className="w-4 h-4 mr-2" />}
                      Test Sandbox Connection
                    </Button>
                  </div>
                </div>

                <hr className="border-slate-200" />

                {/* Production */}
                <div>
                  <EnvSection
                    title="Production Configuration"
                    badge="production"
                    data={config.production}
                    onChange={(k, v) => handleSectionChange('production', k, v)}
                    secretsRevealed={secretsRevealed}
                    onRevealClick={handleRevealClick}
                  />
                  <div className="mt-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={testingProd || !config.production.base_url}
                      onClick={() => handleTest('production')}
                      className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                    >
                      {testingProd ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wifi className="w-4 h-4 mr-2" />}
                      Test Production Connection
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Save */}
            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#0B6445] hover:bg-[#095b3d] text-white px-8"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Configuration
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* 2FA Verification Dialog */}
      <Dialog open={twoFaDialogOpen} onOpenChange={setTwoFaDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0F172A]">
              <ShieldCheck className="w-5 h-5 text-[#0B6445]" />
              Two-Factor Authentication Required
            </DialogTitle>
            <DialogDescription>
              Enter your 2FA code to reveal encrypted API credentials. This action is logged for security audit.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-sm font-medium text-slate-700">Authentication Code</Label>
            <Input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={twoFaCode}
              onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit code"
              className="input-field mt-1 text-center text-lg tracking-[0.3em] font-mono"
              onKeyDown={(e) => { if (e.key === 'Enter' && twoFaCode.length === 6) handleTwoFaVerify(); }}
              autoFocus
            />
            <p className="text-xs text-slate-500 mt-2">
              Open your authenticator app and enter the current code for PayZen.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => { setTwoFaDialogOpen(false); setTwoFaCode(''); }}
              className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
            >
              Cancel
            </Button>
            <Button
              onClick={handleTwoFaVerify}
              disabled={twoFaVerifying || twoFaCode.length < 6}
              className="bg-[#0B6445] hover:bg-[#095b3d] text-white"
            >
              {twoFaVerifying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Verify &amp; Reveal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </BackOfficeLayout>
  );
}
